'use client';

import React, { ReactNode } from 'react';
import Image from 'next/image';
import { HeaderSize, HeaderText } from '@/components/ui/HeaderText';
import formatProductAgeBand from '@/utils/translations/formatProductAgeBand';
import {
  getAssetByKind,
  KIT_ASSET_INDICES,
} from '@/lib/api/productCatalog/helpers/playKitProductAssetHelpers';
import useLocale from '@/hooks/useLocale';
import { useAccordion } from '@/utils/useAccordion';
import { DisplayDateFormat } from '@/utils/dates/constants';
import { formatDateForDisplay } from '@/utils/dates/dateFormatters';
import { GenBanner, GenInternalLink } from '@/components/generics';
import AboutYourPlayKit from './components/AboutYourPlayKit/AboutYourPlayKit';
import { TypePlayKitPageTranslations } from '@/utils/translations/types/TypePlayKitPageTranslations';
import { InsideYourPlayKit } from './components/InsideYourPlayKit';
import {
  buildQueryString,
  constructLegacyCustomerPortalLink,
  SUBSCRIPTION_SOURCE_UUID_KEY,
} from '@/utils/shopSubdomain';
import { AddOnDetails } from './components/AddOnDetails';
import {
  getItemErrors,
  trackClickEvent,
  TypeScheduleDecorateScheduledItem,
} from '@/components/account/PlayKitSchedule/utils';
import { Badge } from '@/components/ui';
import { PROCESS_STATUS } from '@/lib/api/subscriptionManager/types';
import classNames from 'classnames';
import { CTA_NAMES } from '@/utils/rudderstack';
import {
  BannerBorderType,
  BannerColorScheme,
  ICON_DESKTOP_SIZE_MAPPING,
  ICON_MOBILE_SIZE_MAPPING,
  ICON_STYLES,
} from '@/components/generics/GenBanner/GenBanner';
import WarningIcon from '@/components/ui/Icons/WarningIcon';
import { useFlags } from 'launchdarkly-react-client-sdk';

export interface PlayKitCardProps {
  t: TypePlayKitPageTranslations;
  scheduledItem: TypeScheduleDecorateScheduledItem;
  isLastKitInGroup?: boolean;
  children?: ReactNode;
}

function PlayKitCard({
  t,
  scheduledItem,
  isLastKitInGroup = false,
  children,
}: PlayKitCardProps) {
  const locale = useLocale();
  const { name, metadata, assets, description, products } =
    scheduledItem.product || {};

  const {
    'sub-squad-enable-cancel-saver-program': isCancelSaverProgramEnabled,
  } = useFlags();

  // Filter out products that are sub shop only
  const filteredProducts = products?.filter(
    (product) => product.metadata?.data?.sub_shop_only !== 'true',
  );

  const {
    user_uuid,
    subscription_uuid,
    uuid,
    bookBundleProduct,
    isHero,
    isBooksAddonActive,
    isShipDateEditable,
    isShipDateVisible,
    badgeText,
  } = scheduledItem;
  const { age_unit, min_age, max_age } = metadata?.data || {};
  const ageBand = formatProductAgeBand(age_unit, min_age, max_age, locale);
  const playKitImageSrc =
    getAssetByKind(assets, KIT_ASSET_INDICES.NEW_BOX_GRAY_BG)?.uri || '';

  const scheduledDateYear = new Date(
    scheduledItem.scheduled_date,
  ).getFullYear();
  const currentYear = new Date().getFullYear();
  const isDateWithinCurrentYear = scheduledDateYear === currentYear;
  const shippingDate = scheduledItem.scheduled_date
    ? formatDateForDisplay(
        scheduledItem.scheduled_date,
        locale,
        isDateWithinCurrentYear
          ? DisplayDateFormat.ShortMonthAndDay
          : DisplayDateFormat.ShortMonthDayYear,
      )
    : null;

  const isSkipped =
    scheduledItem.process_status === PROCESS_STATUS.SKIP_REQUESTED;
  const errorMessages = getItemErrors(scheduledItem, t);

  const { Icon, Accordion } = useAccordion();

  const ShipDate = () =>
    isShipDateVisible && shippingDate ? (
      <p className="md:text-base text-sm">
        <span className="font-bold">{t.shipDate}</span>
        <span> {shippingDate}</span>
      </p>
    ) : null;

  const _generateEditShipDateLink = () => {
    if (isCancelSaverProgramEnabled) {
      return `/account/subscriptions/${subscription_uuid}/edit-date/${uuid}`;
    }

    return constructLegacyCustomerPortalLink(locale, [
      user_uuid,
      'subscriptions',
      subscription_uuid,
      'edit-ship-date',
      uuid,
    ]);
  };

  const EditShipDate = () =>
    isShipDateVisible && isShipDateEditable && shippingDate ? (
      <GenInternalLink
        classes="md:text-base text-sm"
        href={_generateEditShipDateLink()}
        cta={t.edit}
        onClick={(e) => {
          e.stopPropagation();
          trackClickEvent(CTA_NAMES.EDIT_SHIP_DATE);
        }}
      />
    ) : null;

  const HeroVariant = () => {
    return (
      <div className="flex md:flex-row flex-col gap-6 md:items-center items-start mb-6">
        <Image
          src={playKitImageSrc}
          alt="Play Kit"
          height={192}
          width={256}
          className="h-auto w-full md:w-[256px] bg-blue-light"
        />
        <div className="flex flex-row justify-between items-center md:items-start w-full">
          <div className="text-left">
            {badgeText && (
              <Badge
                classes={classNames('md:hidden !text-xs mb-2', {
                  '!bg-light-light': isSkipped,
                })}
                text={badgeText}
                badgeColor={'Purple'}
              />
            )}
            <div className="flex flex-row gap-2">
              <ShipDate /> <EditShipDate />
            </div>
            <HeaderText classes={'text-4xl'} size={HeaderSize.H2} text={name} />
            <p>{ageBand}</p>
          </div>
          <div className="flex flex-row gap-8">
            {badgeText && (
              <Badge
                classes={classNames('hidden md:block', {
                  '!bg-grey-light': isSkipped,
                })}
                text={badgeText}
                badgeColor={'Purple'}
              />
            )}
            <Icon />
          </div>
        </div>
      </div>
    );
  };

  const skippedStyles = isSkipped ? 'opacity-35' : '';

  const DefaultVariant = () => {
    return (
      <div className="flex flex-row gap-4 md:gap-6 md:items-center items-start mb-6">
        <Image
          src={playKitImageSrc}
          alt="Play Kit"
          height={117}
          width={156}
          className={`hidden md:block h-auto w-[96px] md:w-[156px] ${skippedStyles}`}
        />
        <Image
          src={playKitImageSrc}
          alt="Play Kit"
          height={72}
          width={96}
          className={`block md:hidden h-auto w-[96px] md:w-[156px] ${skippedStyles}`}
        />
        <div className="flex flex-row justify-between gap-2 items-center grow">
          <div className={`text-left ${skippedStyles}`}>
            {badgeText && (
              <Badge
                classes={classNames('md:hidden !text-xs mb-2', {
                  '!bg-grey-light': isSkipped,
                })}
                text={badgeText}
                badgeColor={'Purple'}
              />
            )}
            <div className="flex flex-row gap-2 mb-2">
              <ShipDate /> <EditShipDate />
            </div>
            <HeaderText
              classes={'text-lg leading-5 md:text-d-h3 mb-2'}
              size={HeaderSize.H3}
              text={name}
            />
            <p className="text-sm md:text-base">{ageBand}</p>
          </div>
          <div className="flex flex-row gap-8">
            {badgeText && (
              <Badge
                classes={classNames('hidden md:block', {
                  '!bg-grey-light': isSkipped,
                })}
                text={badgeText}
                badgeColor={'Purple'}
              />
            )}
            <Icon />
          </div>
        </div>
      </div>
    );
  };

  const _generateRemoveBooksAddonLink = () => {
    const queryParameters = [
      {
        key: SUBSCRIPTION_SOURCE_UUID_KEY,
        value: subscription_uuid,
      },
    ];
    const queryString = buildQueryString(queryParameters);
    const link = constructLegacyCustomerPortalLink(
      locale,
      [
        user_uuid,
        'account-settings',
        'cancel-add-on',
        'books_addon',
        subscription_uuid,
      ],
      queryString,
    );
    return link;
  };

  const _generateCustomizeKitLink = () => {
    const link = constructLegacyCustomerPortalLink(locale, [
      user_uuid,
      'subscriptions',
      subscription_uuid,
      'customize-kit',
      scheduledItem.uuid,
    ]);
    return link;
  };

  const ExpandedContent = () => {
    return (
      <div className={isSkipped ? `pointer-events-none ${skippedStyles}` : ''}>
        <AboutYourPlayKit
          title={t.aboutYourPlayKit}
          description={description}
        />
        <InsideYourPlayKit
          title={t.insideYourPlayKit}
          linkCopy={t.replacementParts}
          linkHref="/account/replacement-parts"
          showCustomize={!isHero}
          customizeCopy={t.customize}
          customizeHref={_generateCustomizeKitLink()}
          products={filteredProducts}
        />
        {isBooksAddonActive && bookBundleProduct && (
          <AddOnDetails
            title={t.bookBundleAddOn}
            showLink={!isHero}
            linkCopy={t.remove}
            linkHref={_generateRemoveBooksAddonLink()}
            addOnProducts={bookBundleProduct.components}
          />
        )}
      </div>
    );
  };

  return (
    <div
      data-testid="playKitCard"
      className={`${isLastKitInGroup ? 'pb-0' : 'pb-6'}`}
    >
      {errorMessages.length > 0 &&
        errorMessages.map((message) => (
          <div key={message.description} className="pb-4 md:pb-6">
            <GenBanner
              colorScheme={BannerColorScheme.ERROR}
              borderType={BannerBorderType.SOLID}
              headingAttr={{ size: HeaderSize.H3, text: message.title }}
              paragraphText={message.description}
              icon={
                <>
                  <WarningIcon
                    width={ICON_MOBILE_SIZE_MAPPING[HeaderSize.H3]}
                    height={ICON_MOBILE_SIZE_MAPPING[HeaderSize.H3]}
                    classes="md:hidden"
                    fill={ICON_STYLES[BannerColorScheme.ERROR]}
                  />
                  <WarningIcon
                    width={ICON_DESKTOP_SIZE_MAPPING[HeaderSize.H3]}
                    height={ICON_DESKTOP_SIZE_MAPPING[HeaderSize.H3]}
                    classes="hidden md:block"
                    fill={ICON_STYLES[BannerColorScheme.ERROR]}
                  />
                </>
              }
            />
          </div>
        ))}
      <Accordion
        collapsedContent={isHero ? <HeroVariant /> : <DefaultVariant />}
        expandedContent={<ExpandedContent />}
      />
      {children}
    </div>
  );
}

export default React.memo(PlayKitCard);
